import { isNull } from "@silo/utils";
import { useCallback, useRef, useState } from "react";
import { useBreakpointValue } from "./use-breakpoint-value";

/**
 * @returns
 * - `ref` - attach this ref to a "Load more" element. When it becomes visible in the viewport, the `loadMore` function will be invoked
 * - `pageSize` - recommended responsive page size for loading more data
 */
export const useLoadMore = <T extends Element = HTMLDivElement>(
  loadMore: () => Promise<void>
) => {
  const [isLoading, setIsLoading] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      if (isNull(node)) return;

      observer.current = new IntersectionObserver(async ([entry]) => {
        if (entry.isIntersecting) {
          setIsLoading(true);
          await loadMore();
          setIsLoading(false);
        }
      });

      observer.current.observe(node);
    },
    [isLoading, loadMore]
  );

  const pageSize = useBreakpointValue(1, { sm: 2, md: 6 });

  return { ref, pageSize };
};
